import type { RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { routeName } from '@/constants/route.constants'
import { Role } from '@/constants/role.constants'

/**
 * Main navigation guard for authentication and authorization
 *
 * This guard handles:
 * - Document title setting
 * - Session restoration from localStorage (for page reloads/navigations)
 * - Admin role authentication (requiresAdmin)
 * - General authentication (requiresAuth) - Veterinarian OR VeterinarianUser
 * - Veterinarian-only authentication (requiresVeterinarian)
 * - Public flow guards (terms acceptance, tag status, owner authentication)
 */
export async function authGuard(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  // Set document title from route meta
  if (to.meta.title) {
    document.title = `${to.meta.title} ${import.meta.env.VITE_APP_TITLE}`
  }

  // Get auth store
  const authStore = useAuthStore()

  // Restore session from localStorage if not already authenticated
  // This handles page reloads and direct URL navigation
  if (!authStore.isAuthenticated) {
    await authStore.restoreSession()
  }

  // Handle admin routes
  if (to.meta.requiresAdmin) {
    // Check if authenticated
    if (!authStore.isAuthenticated) {
      return { name: routeName.login, replace: true }
    }

    // Ensure roles are loaded (refresh token if needed)
    await authStore.refreshTokenIfNeeded()

    // Check admin role
    if (!authStore.jwtRoles.includes(Role.Admin)) {
      return { name: routeName.login, replace: true }
    }
  }

  // Handle auth routes (veterinarian or veterinarian user)
  if (to.meta.requiresAuth) {
    // Check if authenticated
    if (!authStore.isAuthenticated) {
      return { name: routeName.login, replace: true }
    }

    // Ensure roles are loaded (refresh token if needed)
    await authStore.refreshTokenIfNeeded()

    // Check veterinarian or veterinarian user role
    if (
      !authStore.jwtRoles.includes(Role.Veterinarian) &&
      !authStore.jwtRoles.includes(Role.VeterinarianUser)
    ) {
      return { name: routeName.login, replace: true }
    }
  }

  // Handle veterinarian-only routes (stricter than requiresAuth)
  if (to.meta.requiresVeterinarian) {
    // Check if authenticated
    if (!authStore.isAuthenticated) {
      return { name: routeName.login, replace: true }
    }

    // Ensure roles are loaded (refresh token if needed)
    await authStore.refreshTokenIfNeeded()

    // Check veterinarian role (not veterinarian user)
    if (!authStore.jwtRoles.includes(Role.Veterinarian)) {
      // If user is a veterinarian user (but not veterinarian), redirect to register page
      if (authStore.jwtRoles.includes(Role.VeterinarianUser)) {
        return { name: routeName.veterinarian.register, replace: true }
      }
      // Otherwise, redirect to login
      return { name: routeName.login, replace: true }
    }
  }

  // Handle public flow guards

  // Terms acceptance required
  if (to.meta.requiresAcceptingTerms) {
    if (!authStore.hasAcceptedTerms) {
      return { name: routeName.public.scan, replace: true }
    }
  }

  // Tag must not have a match (new registration flow)
  if (to.meta.requiresTagHasNoMatch) {
    if (authStore.hasTagGottaMatch) {
      return { name: routeName.public.scan, replace: true }
    }
  }

  // Owner authentication required
  if (to.meta.requiresOwnerAuthentication) {
    if (!authStore.hasOwnerAuthenticated) {
      return { name: routeName.public.scan, replace: true }
    }
  }

  // Allow navigation
  return true
}
